import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Palette, Check, X, Loader2, Info, ArrowLeft } from "lucide-react";
import { z } from "zod";
import { PasswordStrengthIndicator } from "@/components/PasswordStrengthIndicator";
import { useEmailAvailability } from "@/hooks/useEmailAvailability";
import { validatePasswordStrength } from "@/lib/passwordValidation";

const emailSchema = z.string().email("Ogiltig e-postadress");
const passwordSchema = z.string().min(6, "Lösenordet måste vara minst 6 tecken");

export default function Auth() {
  const navigate = useNavigate();
  const { user, ready } = useAuth();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [userType, setUserType] = useState("artist");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupPassword, setSignupPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  
  const { isChecking: checkingEmail, isAvailable: emailAvailable } = useEmailAvailability(signupEmail);

  // Redirect if already logged in
  useEffect(() => {
    if (ready && user) {
      navigate('/home', { replace: true });
    }
  }, [ready, user, navigate]);

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error("Du måste godkänna villkoren för att registrera dig");
      return;
    }
    
    // Check password confirmation
    if (signupPassword !== confirmPassword) {
      toast.error("Lösenorden matchar inte");
      return;
    }
    
    // Validate username
    if (!username || username.trim().length === 0) {
      toast.error("Användarnamn måste anges");
      return;
    }
    
    if (username.length < 3) {
      toast.error("Användarnamnet måste vara minst 3 tecken");
      return;
    }

    // Check email availability
    if (emailAvailable === false) {
      toast.error("Denna e-postadress är redan registrerad");
      return;
    }

    // Check password strength
    const passwordStrength = validatePasswordStrength(signupPassword);
    if (passwordStrength.score < 3) {
      toast.error("Lösenordet är för svagt", {
        description: "Välj ett starkare lösenord enligt kraven",
      });
      return;
    }
    
    try {
      emailSchema.parse(signupEmail);
      passwordSchema.parse(signupPassword);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        return;
      }
    }

    setLoading(true);

    // Check if username already exists
    const { data: existingUser } = await supabase
      .from('profiles')
      .select('username')
      .eq('username', username.trim())
      .maybeSingle();

    if (existingUser) {
      toast.error("Detta användarnamn är redan taget. Välj ett annat.");
      setLoading(false);
      return;
    }

    const { data: authData, error } = await supabase.auth.signUp({
      email: signupEmail,
      password: signupPassword,
      options: {
        data: {
          username: username.trim(),
          user_type: userType,
          seller_type: userType === 'artist' ? 'private' : null,
        },
        emailRedirectTo: `${window.location.origin}/verify-email`,
      },
    });

    if (error) {
      // Check for specific error messages
      if (error.message.includes('already registered')) {
        toast.error("Denna e-postadress är redan registrerad. Försök logga in istället.");
      } else if (error.message.includes('duplicate') || error.message.includes('unique')) {
        toast.error("Användarnamnet eller e-postadressen är redan taget.");
      } else {
        toast.error(error.message);
      }
    } else {
      if (userType === 'artist') {
        toast.success("Kontot har skapats! Din konstnärsprofil kommer granskas av admin innan du kan ladda upp konstverk.", {
          duration: 6000
        });
      } else {
        toast.success("Kontot har skapats! Låt oss komma igång.");
      }
      // Redirect to onboarding after registration
      navigate("/onboarding");
    }
    setLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      emailSchema.parse(email);
      passwordSchema.parse(password);
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast.error(error.errors[0].message);
        return;
      }
    }

    setLoading(true);

    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) {
      toast.error(error.message);
    } else {
      toast.success("Välkommen tillbaka!");
      navigate("/home");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-4">
      <Card className="w-full max-w-md shadow-xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-primary/10 rounded-full">
              <Palette className="h-8 w-8 text-primary" />
            </div>
          </div>
          <CardTitle className="text-2xl">Välkommen till Nordic Art Collective</CardTitle>
          <CardDescription>Upptäck och sälj konst – målningar, fotografier, skulpturer och mer</CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">Logga in</TabsTrigger>
              <TabsTrigger value="signup">Skapa konto</TabsTrigger>
            </TabsList>
            
            <TabsContent value="signin">
              <form onSubmit={handleSignIn} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="signin-email">E-post</Label>
                  <Input
                    id="signin-email"
                    type="email"
                    placeholder="din@email.se"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="signin-password">Lösenord</Label>
                  <Input
                    id="signin-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="text-right">
                  <Link 
                    to="/forgot-password"
                    className="text-sm text-primary hover:underline"
                  >
                    Glömt lösenord?
                  </Link>
                </div>
                <Button type="submit" className="w-full" disabled={loading}>
                  {loading ? "Loggar in..." : "Logga in"}
                </Button>
                <div className="text-center pt-2">
                  <Link 
                    to="/" 
                    className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Tillbaka till startsidan
                  </Link>
                </div>
              </form>
            </TabsContent>
            
            <TabsContent value="signup">
              <form onSubmit={handleSignUp} className="space-y-4">
                {/* 1. E-post */}
                <div className="space-y-2">
                  <Label htmlFor="signup-email">E-post</Label>
                  <div className="relative">
                    <Input
                      id="signup-email"
                      type="email"
                      placeholder="din@email.se"
                      value={signupEmail}
                      onChange={(e) => setSignupEmail(e.target.value)}
                      required
                      className={emailAvailable === false ? "border-destructive" : ""}
                    />
                    {signupEmail && signupEmail.includes('@') && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        {checkingEmail ? (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        ) : emailAvailable === true ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : emailAvailable === false ? (
                          <X className="h-4 w-4 text-destructive" />
                        ) : null}
                      </div>
                    )}
                  </div>
                  {emailAvailable === true && (
                    <p className="text-xs text-green-600 dark:text-green-400">
                      ✓ E-postadressen är tillgänglig
                    </p>
                  )}
                  {emailAvailable === false && (
                    <p className="text-xs text-destructive">
                      ✗ Den här e-postadressen är redan registrerad
                    </p>
                  )}
                </div>

                {/* 2. Lösenord + Bekräfta */}
                <div className="space-y-2">
                  <Label htmlFor="signup-password">Lösenord</Label>
                  <Input
                    id="signup-password"
                    type="password"
                    value={signupPassword}
                    onChange={(e) => setSignupPassword(e.target.value)}
                    required
                  />
                  <PasswordStrengthIndicator password={signupPassword} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Bekräfta lösenord</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className={confirmPassword && confirmPassword !== signupPassword ? "border-destructive" : ""}
                  />
                  {confirmPassword && confirmPassword !== signupPassword && (
                    <p className="text-xs text-destructive">Lösenorden matchar inte</p>
                  )}
                  {confirmPassword && confirmPassword === signupPassword && signupPassword.length > 0 && (
                    <p className="text-xs text-green-600 dark:text-green-400">✓ Lösenorden matchar</p>
                  )}
                </div>

                {/* 3. Användarnamn */}
                <div className="space-y-2">
                  <Label htmlFor="signup-username">Användarnamn</Label>
                  <Input
                    id="signup-username"
                    type="text"
                    placeholder="Välj ett unikt användarnamn"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Andra användare på Nordic Art Collective kommer att se det här namnet.
                  </p>
                </div>

                {/* 5. Kontotyp */}
                <div className="space-y-2">
                  <Label htmlFor="signup-usertype">Jag är</Label>
                  <Select value={userType} onValueChange={setUserType}>
                    <SelectTrigger id="signup-usertype">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="artist">Konstnär (kan också köpa)</SelectItem>
                      <SelectItem value="buyer">Köpare</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Som konstnär kan du både sälja och köpa konst.
                  </p>
                </div>


                {/* 7. Villkor (sammanslagen checkbox) */}
                <div className="flex items-start gap-2 pt-2">
                  <Checkbox
                    id="accept-terms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                  />
                  <label htmlFor="accept-terms" className="text-sm text-muted-foreground cursor-pointer">
                    Jag godkänner Nordic Art Collectives{" "}
                    <a href="/terms" target="_blank" className="text-primary hover:underline">
                      köp- och säljvillkor
                    </a>{" "}
                    och{" "}
                    <a href="/privacy" target="_blank" className="text-primary hover:underline">
                      integritetspolicy
                    </a>
                  </label>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={loading || !acceptedTerms}
                >
                  {loading ? "Skapar konto..." : "Skapa konto"}
                </Button>
                <div className="text-center pt-2">
                  <Link 
                    to="/" 
                    className="inline-flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Tillbaka till startsidan
                  </Link>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
